import { Trans, t } from '@lingui/macro'
import { FormProvider } from 'react-hook-form'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Stack, Typography, Alert, Tooltip } from '@mui/material'
import { MergeType as MergeIcon } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'

import { Patient } from '@/core/models/patient.model'

import { MergeParentsList } from './MergeParentsList'
import { MergeFieldGroup } from './MergeFieldGroup'
import { useMergePatientsForm } from '../model/useMergePatientsForm'

type MergeDetailsDialogProps = {
  open: boolean
  onClose: () => void
  onSubmitSuccess?: () => void
  primaryPatient: Patient
  secondaryPatient: Patient
}

export default function MergeDetailsDialog({ open, onClose, onSubmitSuccess, primaryPatient, secondaryPatient }: MergeDetailsDialogProps) {
  const { isMerging, fieldGroups, rhForm, submitHandler } = useMergePatientsForm({ onSubmitSuccess, primaryPatient, secondaryPatient })

  const handleClose = () => {
    rhForm.reset()
    onClose()
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <MergeIcon />
            <Typography variant="h6">
              <Trans>Review Merge Details</Trans>
            </Typography>
          </Stack>
          <Tooltip title={t`Close`}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <FormProvider {...rhForm}>
          <Stack spacing={3}>
            <Alert severity="warning">
              <Trans>The secondary patient record will be merged into the primary patient and then deleted. This action cannot be undone.</Trans>
            </Alert>
            <MergeFieldGroup fieldGroups={fieldGroups} primaryPatient={primaryPatient} secondaryPatient={secondaryPatient} />
            {primaryPatient && secondaryPatient && <MergeParentsList primaryPatient={primaryPatient} secondaryPatient={secondaryPatient} />}
          </Stack>
        </FormProvider>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} size="large">
          <Trans>Cancel</Trans>
        </Button>
        <Button data-testid="merge-submit" variant="contained" disabled={isMerging} onClick={() => rhForm.handleSubmit(submitHandler)()} size="large" startIcon={<MergeIcon />}>
          {isMerging ? <Trans>Merging...</Trans> : <Trans>Confirm Merge</Trans>}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
